import { motion } from "framer-motion";
import {
  School,
  TrendingUp,
  Target,
  ArrowRight,
  Sparkles,
  CheckCircle,
} from "lucide-react";
import { Card } from "../Card";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "Features/Routes/routeConstants";
import { Button, Flex } from "antd";

export const EmptyCollegeRecommendations = () => {
  const navigate = useNavigate();
  const benefits = [
    {
      icon: <Target className="w-5 h-5 text-blue-500" />,
      title: "AI-Powered Matching",
      description: "Get matched with colleges that fit your profile perfectly",
    },
    {
      icon: <TrendingUp className="w-5 h-5 text-green-500" />,
      title: "Reach, Match & Safety",
      description: "See your chances and build a balanced college list",
    },
    {
      icon: <School className="w-5 h-5 text-purple-500" />,
      title: "Personalized Insights",
      description: "Detailed analysis based on your scores, GPA, and goals",
    },
  ];

  return (
    <Flex className="m-2 w-full" justify="center" align="center">
      <Card
        delay={0.3}
        className="text-center w-[95%] md:w-[90%] h-fit bg-white"
      >
        <div className="text-center p-2 md:p-6 w-[90%]">
          {/* Header with Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#80e280] to-blue-100 rounded-full mb-6"
          >
            <School className="w-10 h-10 text-primary-600" />
          </motion.div>

          {/* Main Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-2xl md:text-3xl font-bold text-gray-900 mb-3"
          >
            Discover Your Perfect College Match
          </motion.h2>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-base md:text-lg text-gray-600 mb-8 max-w-2xl mx-auto"
          >
            Tell us about yourself and get personalized college recommendations
            powered by AI
          </motion.p>

          {/* Benefits Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 max-w-4xl mx-auto"
          >
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-4 text-left">
                <div className="flex items-center space-x-3 mb-2">
                  {benefit.icon}
                  <h3 className="font-semibold text-gray-900">
                    {benefit.title}
                  </h3>
                </div>
                <p className="text-sm md:text-base font-medium text-gray-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mb-4 md:mb-8"
          >
            <Button
              onClick={() => {
                navigate(ROUTES.COLLEGE_FINDER);
              }}
              className="w-full md:w-1/3 h-fit bg-gradient-to-r !hover:bg-gradient-to-r from-[#80e280] to-blue-500  !hover:from-[#0284c7] !hover:to-blue-600 text-white font-semibold p-2 md:px-10 md:py-5 rounded-2xl transition-all duration-200 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center space-x-3"
            >
              <Sparkles className="w-6 h-6" />
              <span>Find My Perfect Colleges</span>
              <ArrowRight className="w-6 h-6" />
            </Button>

            <p className="text-sm md:text-base text-gray-500 mt-4 font-medium">
              <CheckCircle className="w-4 h-4 inline text-green-500 mr-1" />
              Takes only 5 minutes • Free to start
            </p>
          </motion.div>
        </div>
      </Card>
    </Flex>
  );
};
